import { ESystemType, generateFingerprint, getIdentityKeyValue, getSystemTypeFromUrl, IIdentity, ISidebarMenu, ISignAuthInfo, ITopMenu, IUserInfo, queryLoginInfo, querySidebarMenus, querySignAuthInfo, removeToken, setAuthenticateKey, setIdentityKey, switchIdentity, updateSysType } from '@core/api';
import { Router, RouteRecordRaw } from 'vue-router';
import NProgress from 'nprogress';
import { isNull } from '@core/tools';
import { ElMessage } from 'element-plus';
import { onConfirm } from '@vue3/components';
import { addIsThirdProperty, defaultTab, indexPath, updateNullLayoutToDefault, useUserStore } from '../..';
import { useSessionStorage } from '@vueuse/core';

export const setupRouter = async (router: Router, createRoutesFromMenu: (menu: ISidebarMenu[]) => RouteRecordRaw[]): Promise<IRouteMenuData> => {
  NProgress.start();

  let name = '';
  let phone = '';
  let enabled = 0 as 0 | 1;
  let info = {} as IUserInfo;
  let identities: IIdentity[] = [];
  let menuData: ISidebarMenu[] = [];
  let topMenus: ITopMenu[] = [];
  let sideMenus: ISidebarMenu[] = [];
  let permissions: string[] = [];

  const emptyResult: IRouteMenuData = {
    name: '',
    phone: '',
    enabled: 0 as 0 | 1,
    menuData: [],
    identities: [],
    identity: {} as IIdentity,
    permissions: [],
    topMenus: [],
    sideMenus: [],
  };

  const { afterLogout } = useUserStore();

  const logouthandler = (msg: string) => {
    onConfirm({
      title: '温馨提示',
      message: `身份切换失败：${msg}`,
      type: 'warning',
      okText: '确定',
      showCancelButton: false,
      closeOnClickModal: false,
      showClose: false,
      onOk: async () => {
        afterLogout();
      },
    });
  };

  try {
    await generateFingerprint();
    info = await queryLoginInfo();
    identities = info.identities || [];
    name = info.name;
    phone = info.phone;
    enabled = info.enabled;
  } catch (error) {
    ElMessage.error((error as Error).message);
  }

  let switchError = false;
  const systemTypeFromUrl = getSystemTypeFromUrl();
  const identityIdFromCookie = getIdentityKeyValue();
  const firstIdentityId = identities[0]?.identityId;
  let identityId = isNull(identityIdFromCookie) ? firstIdentityId : Number(identityIdFromCookie);
  let systemType = identities.find((item) => item.identityId === identityId)?.systemType;

  if (isNull(identityId) || isNull(systemType)) {
    removeToken();
    logouthandler('未获取到身份id');

    return emptyResult;
  }

  if (!isNull(systemTypeFromUrl) && !isNull(systemType) && systemTypeFromUrl !== systemType) { // 有时候cookie中身份id和url中的系统类型不一致，需要切换身份
    const matchedIdentityId = identities.find((item) => item.systemType === systemTypeFromUrl)?.identityId;

    if (isNull(matchedIdentityId)) {
      console.log('to Login Page from registeRoutes');
      removeToken();
      logouthandler('未获取到匹配的身份id');

      return emptyResult;
    }

    identityId = matchedIdentityId;
    systemType = systemTypeFromUrl;
    updateSysType(String(systemTypeFromUrl));
  }

  try {
    await switchIdentity(identityId);
  } catch (error) {
    // 切换失败，取回默认身份
    switchError = true;

    logouthandler((error as Error).message);
  }
  const signAuth = useSessionStorage<ISignAuthInfo>(
    'signAuth',
    {} as ISignAuthInfo,
  );
  let isforceAuth = false;

  try {
    const { forceAuth, authStatus, failReason, type } = await querySignAuthInfo();

    signAuth.value = {
      forceAuth: forceAuth,
      authStatus: authStatus,
      failReason: failReason,
      type: type,
    };

    if (forceAuth && authStatus !== 3) {
      isforceAuth = true;
    }
  } catch (error) {
    ElMessage.error((error as Error).message);
  }

  if (!switchError) {
    try {
      const withTopMenus = getWithTopMenus(systemType);
      const rs = await querySidebarMenus();

      menuData = rs.menus;
      permissions = rs.permissions;

      addIsThirdProperty(menuData);
      menuData.forEach((v) => updateNullLayoutToDefault(v));

      if (withTopMenus) {
        topMenus = [
          {
            name: indexPath.slice(1),
            routeName: indexPath.slice(1),
            label: defaultTab.title,
            icon: 'mingcute:home-4-line',
          },
          ...getTopMenus(menuData),
        ];
      } else {
        sideMenus = menuData;
      }
    } catch (error) {
      // 切换失败，取回默认身份
      ElMessage.error((error as Error).message);
    }
  }

  const routes = isforceAuth ? [] : createRoutesFromMenu(menuData);

  setIdentityKey(String(identityId));
  setAuthenticateKey(String(isforceAuth));

  NProgress.done();
  routes.forEach((route) => router.addRoute(route));

  identities.forEach((item) => {
    item.identityId = Number(item.identityId) || 0;
  });

  return {
    name,
    phone,
    enabled,
    permissions,
    menuData,
    sideMenus,
    topMenus,
    identities,
    identity: identities.find((item) => item.identityId === identityId) || {} as IIdentity,
  };
};

function getTopMenus(all: ISidebarMenu[]) {
  return all.map((v) => {
    const childs = v.children || [];
    let firstChild = childs[0];

    if (firstChild?.children?.length > 0) {
      firstChild = firstChild.children[0];

      if (firstChild?.children?.length > 0) {
        firstChild = firstChild.children[0];
      }
    }

    const item = {
      name: v.name,
      routeName: firstChild?.name || undefined,
      label: v.meta?.title || '',
      icon: v.meta?.icon || '',
    } as ITopMenu;

    return item;
  });
}

export function getWithTopMenus(systemType: ESystemType) {
  return Number(systemType) === ESystemType.Company; // TODO: 如果是company，处理出top-menus
}

export interface IRouteMenuData {
  /** 用户名 */
  name: string;
  /** 手机号 */
  phone: string;
  /** 是否启用 1 启用，0 禁用 */
  enabled: 0 | 1;
  /** 权限列表 */
  permissions: string[];
  /** 菜单列表 */
  menuData: ISidebarMenu[];
  /** 顶部菜单列表 */
  topMenus: ITopMenu[];
  /** 侧边菜单列表 */
  sideMenus: ISidebarMenu[];
  /** 端对象列表 */
  identities: IIdentity[];
  /** 端对象 */
  identity: IIdentity;
}
